import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, APP_INITIALIZER } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';

class EnvironmentDefinition {
    constructor({ name, endpoints, features, settings }) {
        this.name = name;
        this.endpoints = endpoints !== null && endpoints !== void 0 ? endpoints : [];
        this.features = features !== null && features !== void 0 ? features : [];
        this.settings = settings !== null && settings !== void 0 ? settings : [];
        if (!this.validate()) {
            throw new Error('Invalid EnvironmentDefinition');
        }
    }
    getSettingsMap(prefix = undefined) {
        var _a;
        if (!!prefix) {
            prefix += '.';
        }
        const prefixDepth = (_a = ((prefix === null || prefix === void 0 ? void 0 : prefix.match(/\./g)) || []).length) !== null && _a !== void 0 ? _a : 0;
        const result = {};
        this.settings.filter(s => !!prefix ? s.key.startsWith(prefix) : true).forEach(e => {
            const nodes = e.key.split('.').slice(prefixDepth);
            const valueNode = nodes.pop();
            let node = result;
            nodes.forEach(function (key) {
                node = node[key] = node[key] || {};
            });
            if (valueNode) {
                node[valueNode] = e.value;
            }
        });
        return result;
    }
    validate() {
        try {
            this.getSettingsMap();
        }
        catch (_a) {
            throw new Error(`
      Namespaced settings need to be separated by nodes without values.
      Having 'app.logging.logLevel=debug' and 'app.logging.LogLevel.production=error' would cause a collision.
      A correct example would be 'app.logging.logLevel.default=debug' and 'app.logging.LogLevel.production=error'`);
        }
        return !!this.name && !!this.settings && !!this.endpoints && !!this.features;
    }
    tryValidate() {
        try {
            return this.validate();
        }
        catch (_a) {
            return false;
        }
    }
}

class EnvFeature {
    constructor({ key, state }) {
        this.key = key;
        this.state = state;
    }
}

class EnvSettingKey {
}
EnvSettingKey.App = 'app';
EnvSettingKey.Core = 'app.core';
EnvSettingKey.Features = 'app.features';
EnvSettingKey.Shared = 'app.shared';
EnvSettingKey.TenantCode = 'tenant.code';
EnvSettingKey.AppInsightsInstrumentationKey = 'applicationInsights.instrumentationKey';

const CSH_ENVIRONMENT_DEFINITION = new InjectionToken('CSH_ENVIRONMENT_DEFINITION');
class EnvironmentService {
    constructor(definition) {
        this.definition = definition;
    }
    set(environmentDefinition) {
        this.definition = environmentDefinition;
    }
    getName() {
        if (!this.definition) {
            throw new Error('EnvironmentDefinition hasn\'t been initialized yet.');
        }
        return this.definition.name;
    }
    getSetting(key) {
        var _a;
        return (_a = this.definition) === null || _a === void 0 ? void 0 : _a.settings.find((c) => c.key === key);
    }
    getSettingsMap(prefix = undefined) {
        var _a, _b;
        return (_b = (_a = this.definition) === null || _a === void 0 ? void 0 : _a.getSettingsMap(prefix)) !== null && _b !== void 0 ? _b : {};
    }
    allSettings() {
        var _a;
        return ((_a = this.definition) === null || _a === void 0 ? void 0 : _a.settings) ? this.definition.settings : [];
    }
    getEndpoint(name) {
        var _a;
        return (_a = this.definition) === null || _a === void 0 ? void 0 : _a.endpoints.find((e) => e.name === name);
    }
    allEndpoints() {
        var _a;
        return ((_a = this.definition) === null || _a === void 0 ? void 0 : _a.endpoints) ? this.definition.endpoints : [];
    }
    /**
     *
     * @param key
     * @return {any}
     */
    getFeature(key) {
        var _a;
        return (_a = this.definition) === null || _a === void 0 ? void 0 : _a.features.find(f => f.key === key);
    }
    /**
     *
     * @return {EnvFeature[]}
     */
    allFeatures() {
        var _a, _b;
        return (_b = (_a = this.definition) === null || _a === void 0 ? void 0 : _a.features) !== null && _b !== void 0 ? _b : [];
    }
}
EnvironmentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: EnvironmentService, deps: [{ token: CSH_ENVIRONMENT_DEFINITION, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
EnvironmentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: EnvironmentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: EnvironmentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: EnvironmentDefinition, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CSH_ENVIRONMENT_DEFINITION]
                }] }]; } });

const CSH_ENVIRONMENT_DEFINITION_FILENAME = new InjectionToken('CSH_ENVIRONMENT_DEFINITION_FILENAME');
const CSH_ENVIRONMENT_SCRIPT_FILENAME = new InjectionToken('CSH_ENVIRONMENT_SCRIPT_FILENAME');
class EnvironmentInitializerService {
    constructor(httpClient, environmentService, definitionFilename, scriptFilename) {
        this.httpClient = httpClient;
        this.environmentService = environmentService;
        this.isLoaded = false;
        this.noCacheHeader = {
            headers: new HttpHeaders({
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
                Expires: '0',
            }),
        };
        this.definitionFilename = definitionFilename
            ? definitionFilename
            : 'environment.json';
        if (!!scriptFilename) {
            this.scriptFilename = scriptFilename;
        }
    }
    initialize() {
        var _a;
        const scriptUrl = this.getScriptUrl();
        const requestUrl = `${scriptUrl.protocol}//${scriptUrl.hostname}${scriptUrl.port ? ':' + scriptUrl.port : ''}${scriptUrl.pathname.replace((_a = this.scriptFilename) !== null && _a !== void 0 ? _a : '', '')}`;
        return this.httpClient
            .get(`${requestUrl}${this.definitionFilename}`, this.noCacheHeader)
            .pipe(map((response) => new EnvironmentDefinition(response)))
            .pipe(tap((environmentDefinition) => {
            this.env = environmentDefinition;
            this.environmentService.set(this.env);
            this.isLoaded = true;
        }));
    }
    getScriptUrl() {
        let baseUrl = document.location.href;
        if (!!this.scriptFilename) {
            let scriptElement;
            const scriptElements = document.getElementsByTagName('script');
            for (let i = 0; i < scriptElements.length; i++) {
                const el = scriptElements.item(i);
                if (el === null || el === void 0 ? void 0 : el.src.includes(this.scriptFilename)) {
                    scriptElement = el;
                    break;
                }
            }
            if (!!scriptElement) {
                baseUrl = scriptElement.src;
            }
            // a script tag containing an src attribute including scriptFilename (e.g. fal-saas.js) was not not found
            // check for localhost execution
            else if (baseUrl.includes('localhost:')) {
                // return localhost root (e.g. http://localhost:4200)
                baseUrl = new URL(baseUrl).origin;
            }
        }
        return new URL(baseUrl);
    }
}
EnvironmentInitializerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: EnvironmentInitializerService, deps: [{ token: i1.HttpClient }, { token: EnvironmentService }, { token: CSH_ENVIRONMENT_DEFINITION_FILENAME, optional: true }, { token: CSH_ENVIRONMENT_SCRIPT_FILENAME, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
EnvironmentInitializerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: EnvironmentInitializerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: EnvironmentInitializerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: EnvironmentService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CSH_ENVIRONMENT_DEFINITION_FILENAME]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CSH_ENVIRONMENT_SCRIPT_FILENAME]
                }] }]; } });

class SettingService {
    constructor(environmentService) {
        this.environmentService = environmentService;
    }
    get(key) {
        return this.environmentService.getSetting(key);
    }
    getKey(key) {
        var _a;
        return (_a = this.get(key)) === null || _a === void 0 ? void 0 : _a.key;
    }
    getEnvironmentName() {
        return this.environmentService.getName();
    }
    getValue(key) {
        var _a;
        return (_a = this.get(key)) === null || _a === void 0 ? void 0 : _a.value;
    }
    all() {
        return this.environmentService.allSettings();
    }
    exists(key) {
        return !!this.get(key);
    }
}
SettingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: SettingService, deps: [{ token: EnvironmentService }], target: i0.ɵɵFactoryTarget.Injectable });
SettingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: SettingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: SettingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: EnvironmentService }]; } });

const appEnvironmentInitializer = (environmentInitializerService) => {
    return () => environmentInitializerService.initialize().toPromise();
};
const appEnvironmentInitializerProvider = {
    provide: APP_INITIALIZER,
    useFactory: appEnvironmentInitializer,
    deps: [EnvironmentInitializerService],
    multi: true
};

/**
 * Generated bundle index. Do not edit.
 */

export { CSH_ENVIRONMENT_DEFINITION, CSH_ENVIRONMENT_DEFINITION_FILENAME, CSH_ENVIRONMENT_SCRIPT_FILENAME, EnvFeature, EnvSettingKey, EnvironmentDefinition, EnvironmentInitializerService, EnvironmentService, SettingService, appEnvironmentInitializer, appEnvironmentInitializerProvider };
//# sourceMappingURL=commonspirit-ngx-environment.js.map
